import { Avatar, type AvatarProps } from '../Avatar';
import { useAvatarImageUrl } from '@/hooks';

export interface AvatarGroupItem extends AvatarProps {
  /**
   * Optional storage path for an uploaded profile image. When set, the
   * avatar resolves it to a signed URL via useAvatarImageUrl and renders
   * the image; otherwise it falls back to emoji/initials.
   */
  imagePath?: string | null;
}

export interface AvatarGroupProps {
  avatars: AvatarGroupItem[];
  max?: number;
  size?: 'sm' | 'md';
  className?: string;
}

const sizeMap: Record<string, string> = {
  sm: '-ml-2',
  md: '-ml-3',
};

export function AvatarGroup({
  avatars,
  max = 3,
  size = 'sm',
  className = '',
}: AvatarGroupProps) {
  const visible = avatars.slice(0, max);
  const overflow = avatars.length - max;

  return (
    <div className={['flex items-center', className].filter(Boolean).join(' ')}>
      {visible.map((avatar, i) => (
        <div
          key={i}
          className={[
            'relative ring-2 ring-background rounded-full',
            i > 0 ? sizeMap[size] : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ zIndex: i + 1 }}
        >
          <AvatarGroupItem
            size={size}
            {...avatar}
          />
        </div>
      ))}
      {overflow > 0 && (
        <span
          className={[
            'inline-flex items-center justify-center rounded-full bg-surface-secondary border border-border text-xs font-medium text-muted-foreground',
            sizeMap[size],
            size === 'sm' ? 'w-8 h-8' : 'w-10 h-10',
          ].join(' ')}
          style={{ zIndex: 0 }}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

/**
 * Per-item renderer. Lives in its own component so useAvatarImageUrl gets
 * its own hook state per avatar (safe inside AvatarGroup's map).
 */
function AvatarGroupItem({ imagePath, src, emoji, ...rest }: AvatarGroupItem) {
  const { url } = useAvatarImageUrl(imagePath ?? null);
  const resolvedSrc = url ?? src;
  return (
    <Avatar
      {...rest}
      src={resolvedSrc ?? undefined}
      emoji={resolvedSrc ? undefined : emoji}
    />
  );
}
